// auth-helpers.ts — constitution §V
// Helpers MSAL pour l'acquisition silencieuse de tokens.

import type { AuthenticationResult } from "@azure/msal-browser";
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

import { apiScopes, msalConfig } from "./msal-config";

// Instance MSAL singleton — initialisée au démarrage de l'application
let msalInstance: PublicClientApplication | null = null;
let msalRedirectFailed = false;

export function markMsalRedirectFailed(): void {
  msalRedirectFailed = true;
}

export function hasMsalRedirectFailed(): boolean {
  return msalRedirectFailed;
}

export function clearMsalRedirectFailure(): void {
  msalRedirectFailed = false;
}

export class AuthRedirectInProgressError extends Error {
  constructor() {
    super("MSAL: interaction required, redirecting");
    this.name = "AuthRedirectInProgressError";
  }
}

export function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

/**
 * Acquiert silencieusement un token d'accès pour l'API backend.
 * Si l'acquisition silencieuse échoue (token expiré, consentement manquant),
 * déclenche une interaction (redirect). Ne jamais utiliser localStorage — constitution §V.
 */
export async function getAccessToken(): Promise<string> {
  // Dev mock — bypasse MSAL complètement (VITE_DEV_MOCK_AUTH=true)
  if (import.meta.env.VITE_DEV_MOCK_AUTH === "true") {
    return "dev-mock-token";
  }

  const instance = getMsalInstance();
  // Prefer the active account set during bootstrap; fall back to first account in cache.
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

  if (!account) {
    // No account found — AuthContext will handle the loginRedirect via its own effect.
    throw new Error("MSAL: no active account");
  }

  try {
    const result: AuthenticationResult = await instance.acquireTokenSilent({
      scopes: apiScopes,
      account,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Token expiré ou consentement manquant — interaction nécessaire
      await instance.acquireTokenRedirect({ scopes: apiScopes, account });
      throw new AuthRedirectInProgressError();
    }
    throw error;
  }
}
